import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bookmark, Plus, X, Globe, Package, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { NavBar } from '@/components/NavBar';
import api from '@/lib/api';

const COUNTRIES = [
  { code: 'CN', name: 'China' }, { code: 'VN', name: 'Vietnam' }, { code: 'ID', name: 'Indonesia' },
  { code: 'TH', name: 'Thailand' }, { code: 'MY', name: 'Malaysia' }, { code: 'PH', name: 'Philippines' },
  { code: 'IN', name: 'India' }, { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' },
  { code: 'TW', name: 'Taiwan' }, { code: 'SG', name: 'Singapore' }, { code: 'BD', name: 'Bangladesh' },
  { code: 'KH', name: 'Cambodia' }, { code: 'MM', name: 'Myanmar' }, { code: 'NZ', name: 'New Zealand' },
  { code: 'PG', name: 'Papua New Guinea' }, { code: 'LA', name: 'Laos' },
];

const WatchlistPage = () => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [wl, sl] = await Promise.all([api.getWatchlist(), api.getSuppliers()]);
      setItems(wl.data?.items || []);
      setSuppliers(sl.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const addItem = async (type, code, name) => {
    try {
      await api.addToWatchlist({ type, code, name });
      toast.success(`Added ${name} to watchlist`);
      load();
    } catch (e) { toast.error('Failed to add'); }
  };

  const removeItem = async (id, name) => {
    try {
      await api.removeFromWatchlist(id);
      toast.success(`Removed ${name}`);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) { toast.error('Failed to remove'); }
  };

  const watchedCodes = new Set(items.map(i => i.code));
  const unwatchedCountries = COUNTRIES.filter(c => !watchedCodes.has(c.code));
  const unwatchedSuppliers = suppliers.filter(s => !watchedCodes.has(s.id));
  const filtered = (q, list, key) => q ? list.filter(i => i[key].toLowerCase().includes(q.toLowerCase())) : list;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="watchlist-title">Watchlist</h1>
              <p className="text-[#4E6974] mt-2">Pin countries and suppliers for quick-access monitoring.</p>
            </div>
            <Button onClick={() => setShowAdd(!showAdd)} className="bg-[#00707C] hover:bg-[#00707C]/90" data-testid="watchlist-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Add to watchlist
            </Button>
          </motion.div>

          {/* Add panel */}
          {showAdd && (
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="h-4 w-4 text-[#4E6974]" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search countries or suppliers..."
                    className="flex-1 text-sm outline-none placeholder:text-[#B0BEC5]" data-testid="watchlist-search" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xs font-semibold text-[#4E6974] uppercase tracking-wide mb-2">Countries</h3>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {filtered(search, unwatchedCountries, 'name').map(c => (
                        <button key={c.code} onClick={() => addItem('country', c.code, c.name)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-[#0D3B4F] hover:bg-[#F5F5F5] transition-colors" data-testid={`add-country-${c.code}`}>
                          <Globe className="h-3.5 w-3.5 text-[#4E6974]" /> {c.name}
                          <Plus className="h-3.5 w-3.5 text-[#0097A7] ml-auto" />
                        </button>
                      ))}
                      {unwatchedCountries.length === 0 && <p className="text-xs text-[#4E6974]">All countries are on your watchlist.</p>}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-[#4E6974] uppercase tracking-wide mb-2">Suppliers</h3>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {filtered(search, unwatchedSuppliers, 'supplier_name').map(s => (
                        <button key={s.id} onClick={() => addItem('supplier', s.id, s.supplier_name)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-[#0D3B4F] hover:bg-[#F5F5F5] transition-colors" data-testid={`add-supplier-${s.id}`}>
                          <Package className="h-3.5 w-3.5 text-[#4E6974]" /> {s.supplier_name}
                          <Plus className="h-3.5 w-3.5 text-[#0097A7] ml-auto" />
                        </button>
                      ))}
                      {unwatchedSuppliers.length === 0 && <p className="text-xs text-[#4E6974]">All suppliers are on your watchlist.</p>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Watchlist items */}
          {loading ? <div className="text-center py-20 text-[#4E6974]">Loading watchlist...</div> : items.length === 0 ? (
            <div className="text-center py-20">
              <Bookmark className="h-12 w-12 text-[#4E6974] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">Your watchlist is empty</h2>
              <p className="text-[#4E6974] mb-4">Pin countries and suppliers to track them here.</p>
              <Button onClick={() => setShowAdd(true)} className="bg-[#00707C] hover:bg-[#00707C]/90">Add items</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="watchlist-items">
              {items.map(item => (
                <motion.div key={item.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                  <Card className="bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <Link to={item.type === 'country' ? `/countries/${item.code}` : `/suppliers/${item.code}`}
                          className="flex items-center gap-2 hover:text-[#0097A7] transition-colors">
                          {item.type === 'country' ? <Globe className="h-4 w-4 text-[#0097A7]" /> : <Package className="h-4 w-4 text-[#FF9500]" />}
                          <span className="font-medium text-[#0D3B4F]">{item.name}</span>
                          <Badge variant="outline" className="text-[9px]">{item.type}</Badge>
                        </Link>
                        <button onClick={() => removeItem(item.id, item.name)} className="text-[#B0BEC5] hover:text-[#FF3B30] transition-colors" data-testid={`remove-${item.id}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WatchlistPage;
